import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import type { WebSocketStatus } from '../hooks/useWebSocket';
import type { ServerData, ServerStats, MinecraftServersData, WebSocketMessage, MinecraftServerStats } from '../types/statusMonitor';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { PUBLIC_STATUS_URL, STATUS_MONITOR_HOST_WS_URL, STATUS_MONITOR_MINECRAFT_WS_URL } from '../config/statusMonitor';
import { serverListApiService } from '../services/serverListApi';
import type { ServerListEntry } from '../services/serverListApi';
import { maintenanceApiService, mergeMaintenanceChange } from '../services/maintenanceApi';
import type { MaintenanceOptions, MaintenanceResult } from '../services/maintenanceApi';
import { describeMaintenanceChange } from '../utils/comingSoon';
import { canAdministerMaintenance } from '../utils/maintenancePermissions';
import { canViewModpackConfigs } from '../utils/modpackConfigPermissions';
import { useAuth } from '../contexts/AuthContext';
import { HostStatusTable, MinecraftStatusTable } from '../components/status/StatusTables';
import { FleetStrip, HostStatusOverview, MinecraftStatusOverview } from '../components/status/StatusOverview';
import { buildHostRows, buildServerRows, compareNames, hostKey, selectServerRows } from '../utils/statusMonitorTable';
import type { ServerSortKey, SortDirection, StatusFilter } from '../utils/statusMonitorTable';
import { cn } from '../utils/cn';
import { PageHeader } from '../components/ui/PageHeader';
import { CduAgentStatusProvider } from '../components/status/CduAgentStatus';

function FeedStatus({ name, status, received }: { name: string; status: WebSocketStatus; received: boolean }) {
  const connected = status === 'connected';
  const label = connected ? received ? 'Connected' : 'Waiting for data' : status === 'reconnecting' ? 'Reconnecting' : 'Connecting';
  return <span className="monitor-feed"><span className={`monitor-health-dot monitor-tone-${connected && received ? 'good' : 'warning'}`} />{name} <span className={connected && received ? undefined : 'monitor-tone-warning'}>{label}</span></span>;
}

/** The public server list is the inventory: it names servers the heartbeat feed has forgotten or never met. */
const INVENTORY_REFRESH_MS = 60_000;
// Maintenance is somebody pressing a button, so it is polled on a human
// timescale rather than the inventory's.
const MAINTENANCE_REFRESH_MS = 15_000;
type InventoryState = 'loading' | 'ready' | 'stale' | 'failed';

function InventoryStatus({ state }: { state: InventoryState }) {
  const label = state === 'ready' ? 'Loaded' : state === 'loading' ? 'Loading' : state === 'stale' ? 'Refresh failing' : 'Unavailable';
  return <span className="monitor-feed"><span className={`monitor-health-dot monitor-tone-${state === 'ready' ? 'good' : 'warning'}`} />Server list <span className={state === 'ready' ? undefined : 'monitor-tone-warning'}>{label}</span></span>;
}

export const StatusMonitorView = () => {
  useDocumentTitle('Status Monitor');
  const [servers, setServers] = useState<ServerData>({});
  const [minecraftServers, setMinecraftServers] = useState<MinecraftServersData>({});
  const [receivedHosts, setReceivedHosts] = useState(false);
  const [receivedMinecraft, setReceivedMinecraft] = useState(false);
  const [now, setNow] = useState(Date.now);
  // Other pages link here with ?cluster= (the feed's cluster id, which is the
  // config's cluster name) and ?q= (an address), so a config's "status" link
  // lands on exactly its servers. Read once; the page owns the filters after.
  const [params] = useSearchParams();
  const [view, setView] = useState<'servers' | 'hosts'>('servers');
  const [layout, setLayout] = useState<'overview' | 'table'>('overview');
  const [query, setQuery] = useState(() => params.get('q') ?? '');
  const [host, setHost] = useState('');
  const [cluster, setCluster] = useState<string | null>(() => params.get('cluster'));
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<ServerSortKey>('health');
  const [direction, setDirection] = useState<SortDirection>('asc');
  const [arrangement, setArrangement] = useState<'flat' | 'clusters' | 'problems'>('problems');
  const [inventory, setInventory] = useState<ServerListEntry[] | null>(null);
  const [inventoryState, setInventoryState] = useState<InventoryState>('loading');
  // The flags and the coming-soon subset travel together, so a server is never
  // drawn as one kind of window by one map and the other by the second.
  const [windows, setWindows] = useState<Pick<MaintenanceResult, 'servers' | 'comingSoon'> | null>(null);
  const [maintenanceNote, setMaintenanceNote] = useState<string | null>(null);
  const { identity } = useAuth();
  const canMaintain = canAdministerMaintenance(identity);
  const configLinks = canViewModpackConfigs(identity);
  const groupByCluster = arrangement !== 'flat';

  // The last good list stays in use when a refresh fails; the chip says so rather than dropping rows.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const list = await serverListApiService.getServerList({ maxAge: INVENTORY_REFRESH_MS });
        if (cancelled) return;
        setInventory(list);
        setInventoryState('ready');
      } catch {
        if (!cancelled) setInventoryState(previous => previous === 'ready' || previous === 'stale' ? 'stale' : 'failed');
      }
    };
    void load();
    const timer = setInterval(load, INVENTORY_REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // Maintenance is polled faster than the inventory: it changes by somebody
  // pressing a button, and everyone watching should see the window open within
  // seconds rather than at the next minute boundary. A failed read keeps the
  // last answer rather than clearing the overlays, because dropping them would
  // redraw a planned window as an outage.
  const readMaintenance = useCallback(async () => {
    try {
      const result = await maintenanceApiService.getMaintenance();
      setWindows({ servers: result.servers, comingSoon: result.comingSoon });
    } catch {
      setWindows(previous => previous);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = () => { if (!cancelled) void readMaintenance(); };
    poll();
    const timer = setInterval(poll, MAINTENANCE_REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [readMaintenance]);

  const applyMaintenance = useCallback(async (addresses: string[], next: boolean, options: MaintenanceOptions = {}) => {
    // Whether an end opens servers that were coming soon, so the note can say
    // "opened to the public" rather than "taken out of maintenance".
    const wasComingSoon = !next && addresses.some(address => windows?.comingSoon[address.trim().toLowerCase()]);
    const result = await maintenanceApiService.setMaintenance(addresses, next, options);
    // Paint the change before the next poll confirms it, so the press feels
    // immediate; the poll is still what makes it true for everyone else.
    const requested = { maintenance: next, ...options };
    setWindows(previous => mergeMaintenanceChange(previous, result, requested));
    setMaintenanceNote(describeMaintenanceChange(result, requested, wasComingSoon));
    void readMaintenance();
  }, [readMaintenance, windows]);

  // Age reports even when an agent or its entire feed stops sending messages.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);

  const handleServerMessage = useCallback((msg: WebSocketMessage) => {
    if (msg.type === 'init' && msg.data) {
      setServers(msg.data as ServerData);
      setReceivedHosts(true);
    }
    if (msg.type === 'update' && msg.id && msg.data) {
      const id = msg.id;
      setServers(prev => ({ ...prev, [id]: msg.data as ServerStats }));
      setReceivedHosts(true);
    }
  }, []);

  const handleMinecraftMessage = useCallback((msg: WebSocketMessage) => {
    if (msg.type === 'minecraft_init' && msg.minecraft) {
      setMinecraftServers(msg.minecraft);
      setReceivedMinecraft(true);
    }
    if (msg.type === 'minecraft_update' && msg.id && msg.data) {
      const id = msg.id;
      setMinecraftServers(prev => ({ ...prev, [id]: msg.data as MinecraftServerStats }));
      setReceivedMinecraft(true);
    }
    if (msg.type === 'minecraft_delete' && msg.id) {
      const id = msg.id;
      setMinecraftServers(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  }, []);

  const hostConnection = useWebSocket<WebSocketMessage>(STATUS_MONITOR_HOST_WS_URL, handleServerMessage);
  const minecraftConnection = useWebSocket<WebSocketMessage>(STATUS_MONITOR_MINECRAFT_WS_URL, handleMinecraftMessage);
  // Listed servers are only filled in once the feed has sent its snapshot, so a slow socket never reads as a fleet outage.
  const rows = useMemo(() => buildServerRows(minecraftServers, now, receivedMinecraft && inventory ? inventory : undefined, windows?.servers ?? null, windows?.comingSoon ?? null), [minecraftServers, now, receivedMinecraft, inventory, windows]);
  const hosts = useMemo(() => buildHostRows(servers, rows, now), [servers, rows, now]);
  const hostOptions = useMemo(() => [...new Set([host, ...rows.map(row => row.host), ...hosts.map(row => hostKey(row.name))])].filter(Boolean).sort(compareNames), [rows, hosts, host]);
  const visibleServers = useMemo(() => selectServerRows(rows, query, host, filter, sort, direction).filter(row => cluster === null || (row.stats.clusterId || '') === cluster), [rows, query, host, filter, sort, direction, cluster]);
  const visibleHosts = useMemo(() => hosts.filter(row => (
    `${row.name} ${hostKey(row.name)}`.toLowerCase().includes(query.trim().toLowerCase()) &&
    (filter === 'all' || (filter === 'attention' && row.health !== 'online') || (filter === 'offline' && !row.online))
  )), [hosts, query, filter]);
  const offline = rows.filter(row => row.health === 'offline').length;
  const attention = rows.filter(row => row.health === 'attention').length;
  const players = rows.reduce((total, row) => total + (row.online && Number.isFinite(row.stats.playerCount) ? row.stats.playerCount : 0), 0);
  const hasFilters = Boolean(query || (view === 'servers' && (host || cluster !== null)) || filter !== 'all');
  const received = view === 'servers' ? receivedMinecraft : receivedHosts;
  const connection = view === 'servers' ? minecraftConnection : hostConnection;
  const count = view === 'servers' ? rows.length : hosts.length;
  const visibleCount = view === 'servers' ? visibleServers.length : visibleHosts.length;
  const disconnected = connection !== 'connected';
  const highestQuasarVersion = useMemo(() => rows.reduce((latest, row) => {
    const version = row.stats.quasarVersion || '';
    return compareNames(version, latest) > 0 ? version : latest;
  }, ''), [rows]);

  const resetFilters = () => { setQuery(''); setHost(''); setCluster(null); setFilter('all'); };
  const showHost = (hostname: string) => { setView('servers'); setHost(hostname); setCluster(null); setQuery(''); setFilter('all'); };
  const changeSort = (key: ServerSortKey) => {
    if (key === sort) setDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setDirection(key === 'players' || key === 'mspt' || key === 'backup' || key === 'seen' ? 'desc' : 'asc'); }
  };

  const emptyState = visibleCount === 0 ? <div className="py-10 text-center whitespace-normal" role="status">
    <p className="font-medium text-[var(--text-primary)]">{!received ? connection === 'reconnecting' ? 'Status feed unavailable' : 'Waiting for status reports…' : count === 0 ? `No ${view === 'servers' ? 'Minecraft servers' : 'hosts'} reported` : 'No matching results'}</p>
    <p className="mt-2 text-sm text-[var(--text-secondary)]">{!received ? 'Reports will appear automatically when the feed is available.' : count === 0 ? 'No entries in the latest report. New reports will appear automatically.' : 'Try a different search or clear the filters.'}</p>
    {hasFilters && <button type="button" className="btn btn-ghost mt-3" onClick={resetFilters}>Clear filters</button>}
  </div> : undefined;

  return <CduAgentStatusProvider hosts={servers} now={now}><div className="status-monitor">
    <PageHeader className="monitor-page-header" title="Status monitor" description={<span className="monitor-feeds" role="status"><FeedStatus name="Minecraft" status={minecraftConnection} received={receivedMinecraft} /><FeedStatus name="Hosts" status={hostConnection} received={receivedHosts} /><InventoryStatus state={inventoryState} /></span>} actions={<>
    <div className="monitor-summary" aria-label="Fleet totals">
      {[
        { label: 'Minecraft servers', value: rows.length, chosen: 'all' as const },
        { label: 'Offline', value: offline, chosen: 'offline' as const },
        { label: 'Needs attention', value: attention + offline, chosen: 'attention' as const },
      ].map(item => <button key={item.label} type="button" className={cn(item.chosen === 'offline' && offline > 0 ? 'status-danger' : item.chosen === 'attention' && item.value > 0 ? 'status-warning' : undefined)} onClick={() => { setView('servers'); resetFilters(); setFilter(item.chosen); }}>
        <strong>{receivedMinecraft ? item.value : '—'}</strong><span>{item.label}</span>
      </button>)}
      <span><strong>{receivedMinecraft ? players.toLocaleString() : '—'}</strong><span>Players online</span></span>
    </div>
      <a href={`${PUBLIC_STATUS_URL}/minecraft`} target="_blank" rel="noopener noreferrer" className="glass-button-secondary glass-button-sm">Public status <span aria-hidden="true">↗</span></a>
    </>} />
    <section aria-label="Fleet status">
      <div className="monitor-navigation">
        <div className="tabs monitor-view-tabs" role="group" aria-label="Status view">
          <button type="button" className="tab" data-active={view === 'servers'} aria-pressed={view === 'servers'} onClick={() => { setView('servers'); setFilter('all'); }}>Minecraft <span>{receivedMinecraft ? rows.length : '—'}</span></button>
          <button type="button" className="tab" data-active={view === 'hosts'} aria-pressed={view === 'hosts'} onClick={() => { setView('hosts'); setFilter('all'); }}>Hosts <span>{receivedHosts ? hosts.length : '—'}</span></button>
        </div>
        <div className="segmented monitor-layout-tabs" role="group" aria-label="Display layout">
          <button type="button" className="segmented-item segmented-item-sm" data-active={layout === 'overview'} aria-pressed={layout === 'overview'} onClick={() => setLayout('overview')}>Overview</button>
          <button type="button" className="segmented-item segmented-item-sm" data-active={layout === 'table'} aria-pressed={layout === 'table'} onClick={() => setLayout('table')}>Table</button>
        </div>
      </div>
      {view === 'servers' && receivedMinecraft && rows.length > 0 && <div className="monitor-fleet">
        <div className="monitor-fleet-label"><span>Fleet at a glance · one mark per server</span><div><span><i className="monitor-health-dot monitor-tone-good" />Healthy</span><span><i className="monitor-health-dot monitor-tone-warning" />Attention</span><span><i className="monitor-health-dot monitor-tone-danger" />Critical / offline</span>{rows.some(row => row.comingSoon) && <span><i className="monitor-health-dot monitor-tone-soon" />Coming soon</span>}</div></div>
        <FleetStrip rows={rows} selected={cluster} clusterOrder={arrangement === 'problems' ? 'problems' : 'name'} onCluster={id => { resetFilters(); setCluster(cluster === id ? null : id); }} />
      </div>}
      <div className="glass-card monitor-toolbar">
        <label className="monitor-search"><span className="sr-only">Search {view}</span><input className="glass-input" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={view === 'servers' ? 'Search servers, modpacks or hosts…' : 'Search hosts…'} /></label>
        {view === 'servers' && <label><span className="sr-only">Filter by host</span><select className="glass-select" value={host} onChange={event => setHost(event.target.value)}><option value="">All hosts</option>{hostOptions.map(name => <option key={name} value={name}>{name}</option>)}</select></label>}
        <label><span className="sr-only">Filter by status</span><select className="glass-select" value={filter} onChange={event => setFilter(event.target.value as StatusFilter)}>
          <option value="all">All statuses</option><option value="attention">Needs attention</option><option value="offline">Offline</option>
          {view === 'servers' && <><option value="silent">No status report</option><option value="maintenance">In maintenance</option><option value="coming-soon">Coming soon</option><option value="performance">Low TPS / high MSPT</option><option value="backup">Stale backups</option><option value="outdated">Quasar behind fleet</option></>}
        </select></label>
        {view === 'servers' && <label className="monitor-sort"><span className="sr-only">Group and sort servers</span><select className="glass-select" value={arrangement} onChange={event => setArrangement(event.target.value as typeof arrangement)}><option value="problems">Problem clusters first</option><option value="clusters">Clusters: A–Z</option><option value="flat">All servers, ungrouped</option></select></label>}
        <button type="button" className="btn-ghost glass-button-sm" disabled={!hasFilters} onClick={resetFilters}>Clear</button>
      </div>
      {cluster !== null && view === 'servers' && <div className="monitor-active-filter"><span>Cluster: {cluster || 'Unassigned'}</span><button type="button" onClick={() => setCluster(null)}>Show all clusters</button></div>}
      {maintenanceNote && <div className="monitor-active-filter" role="status"><span>{maintenanceNote}</span><button type="button" onClick={() => setMaintenanceNote(null)}>Dismiss</button></div>}
      {disconnected && <div className="mx-4 mb-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-hover)] px-3 py-2 text-sm text-[var(--tone-amber-text)]" role="status"><p>{connection === 'reconnecting' ? 'Feed disconnected. Reconnecting automatically.' : 'Connecting to the status feed…'} {received && 'Showing last known reports; readings may be out of date.'}</p></div>}
      {layout === 'overview' ? <>{view === 'servers'
        ? <MinecraftStatusOverview rows={visibleServers} now={now} onHost={showHost} groupByCluster={groupByCluster} clusterOrder={arrangement === 'problems' ? 'problems' : 'name'} onMaintenance={canMaintain ? applyMaintenance : undefined} configLinks={configLinks} />
        : <HostStatusOverview rows={visibleHosts} now={now} onHost={showHost} />}{emptyState}</>
        : view === 'servers' ? <MinecraftStatusTable rows={visibleServers} now={now} sort={sort} direction={direction} onSort={changeSort} onHost={showHost} highestQuasarVersion={highestQuasarVersion} groupByCluster={groupByCluster} clusterOrder={arrangement === 'problems' ? 'problems' : 'name'} configLinks={configLinks}>{emptyState}</MinecraftStatusTable>
        : <HostStatusTable rows={visibleHosts} now={now} onHost={showHost}>{emptyState}</HostStatusTable>}
      <div className="monitor-footer">
        <span aria-live="polite">{received ? `${visibleCount} of ${count} ${view === 'servers' ? 'servers' : 'hosts'}` : 'Awaiting reports'}{view === 'servers' && host ? ` · ${host}` : ''}{view === 'servers' && groupByCluster ? ' · Sorted within clusters' : ''}</span>
        <span>Host / Minecraft heartbeat: delayed after 3m, offline after 5m · CDU Agent: offline after 90s{view === 'servers' ? ' · TPS < 18 · MSPT > 50 · Backup > 24h · Listed servers without a heartbeat come from the server list' : ' · CPU / memory > 90% · Disk > 85% · Closed services'}</span>
      </div>
    </section>
  </div></CduAgentStatusProvider>;
};
